import axios from 'axios'
import store from '@/store'
import { message } from 'ant-design-vue'

// 防止多个请求同时刷新token 
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

const request = axios.create({
  baseURL: '/api',
  timeout: 30000
})

// 主动刷新token函数
async function refreshTokenIfNeeded() {
  const isTokenExpiringSoon = store.getters['auth/isTokenExpiringSoon']
  const isTokenExpired = store.getters['auth/isTokenExpired']
  const isRefreshTokenExpired = store.getters['auth/isRefreshTokenExpired']
  
  if (isRefreshTokenExpired) {
    store.dispatch('auth/logout')
    window.location.href = '/login'
    throw new Error('刷新令牌已过期')
  }
  
  if (isTokenExpiringSoon || isTokenExpired) {
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject })
      })
    }
    
    isRefreshing = true
    
    try {
      const response = await store.dispatch('auth/refreshToken')
      processQueue(null, response.access)
      return response.access
    } catch (error) {
      processQueue(error, null)
      store.dispatch('auth/logout')
      message.error('登录已过期，请重新登录')
      window.location.href = '/login'
      throw error
    } finally {
      isRefreshing = false
    }
  }
  
  return null
}

// 请求拦截器
request.interceptors.request.use(
  async config => {
    const token = store.state.auth.token
    
    if (token) {
      // 检查token是否需要刷新
      try {
        await refreshTokenIfNeeded()
        const newToken = store.state.auth.token
        if (newToken) {
          config.headers.Authorization = `Bearer ${newToken}`
        }
      } catch (error) {
        return Promise.reject(error)
      }
    }
    
    // 如果是 FormData，不要手动设置 Content-Type，让 axios 自动设置
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 响应拦截器
request.interceptors.response.use(
  response => {
    // 如果是 blob 响应类型，返回完整的 response 对象
    if (response.config.responseType === 'blob') {
      return response
    }
    return response.data
  },
  async error => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      // 如果已经有请求正在刷新token，将当前请求加入队列
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return request(originalRequest)
        }).catch(err => {
          return Promise.reject(err)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        // 尝试刷新token
        const response = await store.dispatch('auth/refreshToken')
        const newToken = response.access

        // 处理队列中的请求
        processQueue(null, newToken)

        // 使用新token重新发送原请求
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return request(originalRequest)
      } catch (refreshError) {
        // 刷新失败，清除所有状态并跳转登录
        processQueue(refreshError, null)
        store.dispatch('auth/logout')
        message.error('登录已过期，请重新登录')
        window.location.href = '/login'
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    // 非401错误，正常处理
    if (error.response) {
      switch (error.response.status) {
        case 400:
          // 400错误已经在业务代码中处理，这里不再显示任何错误
          break
        case 401:
          // 已经在上面的逻辑中处理了，这里只是防止重复
          if (!originalRequest._retry) {
            message.error('登录已过期，请重新登录')
            store.dispatch('auth/logout')
            window.location.href = '/login'
          }
          break
        case 403: {
          const errorMsg = error.response.data?.error || 
                          error.response.data?.detail || 
                          '没有权限访问'
          message.error(errorMsg)
          break
        }
        case 404:
          message.error('请求的资源不存在')
          break
        case 500:
          message.error('服务器错误，请稍后重试')
          break
        default:
          message.error('请求失败')
      }
    } else {
      message.error('网络错误，请检查网络连接')
    }
    return Promise.reject(error)
  }
)

export default request
